// pages/ProjectTemplates.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Edit, Trash2, FileText } from "lucide-react";
import apiService from "../../services/api";
import CreateProjectTemplateModal from "../../components/CreateProjectTemplateModal";
import { useTheme } from "../../contexts/ThemeContext";

const ProjectTemplates = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this template? This action cannot be undone.",
      )
    ) {
      try {
        await apiService.deleteProjectTemplate(templateId);
        fetchTemplates();
      } catch (error) {
        console.error("Error deleting template:", error);
        alert("Failed to delete template");
      }
    }
  };

  const filteredTemplates = templates.filter((template) =>
    template.projectTemplateName
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Project Templates
            </h1>
            <p
              className={`text-sm sm:text-base ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Create and manage project templates with predefined tasks
            </p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white whitespace-nowrap"
          >
            <Plus size={16} />
            New Template
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg transition-colors focus:outline-none ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-white placeholder-gray-400 focus:border-gray-600" : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-gray-400"}`}
            />
            <Search
              size={16}
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
            />
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div
            className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Loading...
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <FileText
              size={64}
              className={`mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
            />
            <p
              className={`mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              No project templates found
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
            >
              Create First Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className={`rounded-lg p-4 transition-colors cursor-pointer ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 hover:border-gray-600" : "bg-white border border-gray-200 hover:border-gray-300 shadow-sm"}`}
                onClick={() => navigate(`/project-templates/${template.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3
                    className={`font-semibold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {template.projectTemplateName}
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/project-templates/${template.id}`);
                      }}
                      className={`p-1 rounded transition-colors ${isDarkMode ? "text-gray-400 hover:text-blue-400" : "text-gray-500 hover:text-blue-600"}`}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(template.id);
                      }}
                      className={`p-1 rounded transition-colors ${isDarkMode ? "text-gray-400 hover:text-red-400" : "text-gray-500 hover:text-red-500"}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {template.templateDescription && (
                  <p
                    className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {template.templateDescription}
                  </p>
                )}

                <div className="flex items-center justify-between text-sm">
                  <div
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    {template.taskTemplates?.length || 0} tasks
                  </div>
                  <div
                    className={`px-2 py-1 rounded text-xs ${
                      template.isActive
                        ? "bg-green-500/20 text-green-400"
                        : "bg-gray-500/20 text-gray-400"
                    }`}
                  >
                    {template.isActive ? "Active" : "Inactive"}
                  </div>
                </div>

                {template.owner && (
                  <div
                    className={`mt-3 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Owner: {template.owner.name}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateProjectTemplateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
};

export default ProjectTemplates;
